import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import { colors } from 'bench-style-constants';
import formatCurrency from 'format-currency';
import { Button, Icon, Label } from 'primitive-components';
import { TransactionSidebarSegmentingZeroState } from 'client-web-app';

import openSegmentCategoryManagerModal from './openSegmentCategoryManagerModal';
import SegmentInput from './SegmentInput';
import openBulkSegmentAllocatorAlertModal from './openBulkSegmentAllocatorAlertModal';

const TransactionInfoContainer = styled.div`
  padding: 16px 24px;
`;

const FlexContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const PaddedSpan = styled.span`
  padding-top: 8px;
`;

const ContextContainer = styled.div`
  align-items: center;
  background: ${colors.pearl};
  box-shadow: 0 -1px 0 0 ${colors.silt};
  display: flex;
  padding: 8px 24px;
  font-size: 12px;
  width: 100%;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const SegmentInputsContainer = styled.div`
  padding: 0 24px 16px 24px;
`;

const SegmentInputContainer = styled.div`
  padding: 24px 0 0 0;
`;

const ActionContainer = styled.div`
  align-items: center;
  box-shadow: 0 -1px 0 0 ${colors.silt};
  display: flex;
  font-size: 12px;
  justify-content: space-between;
  padding: 16px 24px;
`;

const WarningContainer = styled.div`
  box-shadow: 0 -1px 0 0 ${colors.silt};
  display: block;
  font-size: 12px;
  padding: 16px 24px;
`;

const WarningMessage = styled.div`
  align-items: center;
  color: ${colors.crown};
  display: flex;
  padding-bottom: 16px;

  span {
    padding-left: 6px;
  }
`;

const WarningIcon = styled(Icon)`
  fill: ${colors.crown};
  flex-basis: 40px;
  width: 16px;
  height: 16px;
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const CancelButton = styled(Button)`
  color: ${colors.smoke};
  margin-right: 8px;
  padding-right: 16px;
  padding-left: 16px;
`;

const ErrorSection = styled.div`
  align-items: center;
  border-radius: 4px;
  box-shadow: inset 0 0 0 1px #f16549;
  background-color: ${colors.pearl};
  color: ${colors.copper};
  display: flex;
  font-size: 12px;
  margin: 8px 24px;
  span {
    padding: 8px;

    svg {
      fill: ${colors.pearl};
      height: 16px;
      width: 16px;
    }

    &:first-child {
      background: #f16549;
      border-top-left-radius: 4px;
      border-bottom-left-radius: 4px;
    }
  }
`;

/**
 * @typedef {Object<string, any>} CategoryTagSelection
 * @property {string} categoryId The list of data entries to update
 * @property {string} segmentId The list of data entries to update
 */

/**
 * @typedef {Object<string, any>} BulkAllocationRequest
 * @property {string[]} dataEntryIds The list of data entries to update
 * @property {CategoryTagSelection[]} categoryTagSelections A list of category/segment pairs
 */

/**
 * @callback onSaveFunction
 * @param {BulkAllocationRequest}
 * @return {Promise}
 */

/**
 * @typedef {Object<string, any>} BulkSegmentAllocatorProps
 * @property {Array.<Object>} dataEntries The list of data entries to apply tags to
 * @property {Array.<Object>} isOverridingSegments Whether existing data entry
 *  tags will be overridden.
 * @property {function(): void} onCancel Handle bulk save cancellation
 * @property {function(): void} onDone Handle bulk allocation completed
 * @property {onSaveFunction} onSave Handle bulk save request
 */

/**
 * @typedef {Object<string, any>} BulkSegmentAllocatorState
 * @property {Object[]} clientCategories List of client categories and their segments
 * @property {CategoryTagSelection} selected category/segment pairs
 * @property {boolean} tagsSaved Whether the tags were successfully saved
 */

/**
 * @class
 * @property {BulkSegmentAllocatorProps} props
 * @property {BulkSegmentAllocatorState} state
 */
class BulkSegmentAllocator extends React.Component {
  constructor() {
    super();
    this.state = {
      errorSavingTags: false,
      selections: [],
      tagsSaved: false,
    };
  }

  buildSegmentInputOptions(categorySegments) {
    return categorySegments.map(segment => ({
      label: segment.name,
      value: segment.id,
    }));
  }

  buildChangeHandler(categoryId) {
    return clientSegmentId => {
      const filteredSelections = this.state.selections.filter(s => s.categoryId !== categoryId);
      this.setState({
        errorSavingTags: false,
        selections: [...filteredSelections, { categoryId, segmentId: clientSegmentId }],
        tagsSaved: false,
      });
    };
  }

  buildDeleteHandler(categoryId) {
    return () => {
      const filteredSelections = this.state.selections.filter(s => s.categoryId !== categoryId);
      this.setState({
        errorSavingTags: false,
        selections: filteredSelections,
        tagsSaved: false,
      });
    };
  }

  handleCancel() {
    if (!this.state.selections.length) {
      this.props.onCancel();
      return;
    }

    openBulkSegmentAllocatorAlertModal({
      confirmButtonText: `Confirm`,
      message: `You have tags you've selected but haven't applied yet—cancelling will remove those tags. This cannot be undone.`,
      title: `Are you sure you want to cancel\ntagging transactions?`,
      onConfirm: this.props.onCancel,
    });
  }

  handleOverrideTags() {
    openBulkSegmentAllocatorAlertModal({
      confirmButtonText: `Overwrite Tags`,
      message: `The selected transactions already have tags applied. If you apply new tags it will overwrite the existing ones. This cannot be undone.`,
      title: `Are you sure you want to overwrite\nexisting tags with new ones?`,
      onConfirm: () => this.saveSelectedTags(),
    });
  }

  async saveSelectedTags() {
    this.setState({
      errorSavingTags: false,
      tagsSaved: false,
    });

    try {
      await this.props.onSave(this.state.selections);
      this.setState({ tagsSaved: true });
    } catch (e) {
      this.setState({ errorSavingTags: true });
    }
  }

  render() {
    const { clientCategories, clientId, dataEntries, isOverridingSegments, onDone } = this.props;

    const { errorSavingTags, tagsSaved, selections } = this.state;

    const cancelButton = (
      <CancelButton onClick={() => this.handleCancel()} size="small" subtle>
        Cancel
      </CancelButton>
    );

    return (
      <div>
        <TransactionInfoContainer>
          <FlexContainer>
            <Label>Selected</Label>
            <Label>Total</Label>
          </FlexContainer>
          <FlexContainer>
            <PaddedSpan>{dataEntries.length} transactions</PaddedSpan>
            <PaddedSpan>
              {formatCurrency(dataEntries.reduce((total, t) => total + t.amount, 0))}
            </PaddedSpan>
          </FlexContainer>
        </TransactionInfoContainer>
        <ContextContainer>
          <Icon icon="exclamation" /> &nbsp;Tag transactions to get detailed reports on your Income
          Statement.
        </ContextContainer>
        {!clientCategories.length && (
          <TransactionSidebarSegmentingZeroState
            onSetupSegmenting={() => openSegmentCategoryManagerModal(clientId)}
          />
        )}
        {!!clientCategories.length && (
          <SegmentInputsContainer>
            {clientCategories.map(category => (
              <SegmentInputContainer key={category.id}>
                <SegmentInput
                  categoryName={category.name}
                  onChange={this.buildChangeHandler(category.id)}
                  onDelete={this.buildDeleteHandler(category.id)}
                  options={this.buildSegmentInputOptions(category.segments)}
                />
              </SegmentInputContainer>
            ))}
          </SegmentInputsContainer>
        )}
        {isOverridingSegments && !tagsSaved && (
          <WarningContainer>
            <WarningMessage>
              <WarningIcon icon="exclamation" />
              <span>
                The selected transactions already have tags applied. If you apply new tags you will
                overwrite the existing ones.
              </span>
            </WarningMessage>
            <ActionButtons>
              {cancelButton}
              <Button
                disabled={!selections.length}
                onClick={() => this.handleOverrideTags()}
                size="small"
              >
                Apply New Tags
              </Button>
            </ActionButtons>
          </WarningContainer>
        )}
        {!!selections.length && !isOverridingSegments && !tagsSaved && (
          <ActionContainer>
            <span>New tags have been added</span>
            <ActionButtons>
              {cancelButton}
              <Button onClick={() => this.saveSelectedTags()} size="small" styleType="primary">
                Apply New Tags
              </Button>
            </ActionButtons>
          </ActionContainer>
        )}
        {tagsSaved && (
          <ActionContainer>
            <span>Tags have been applied to the selected transactions</span>
            <Button size="small" onClick={() => onDone()}>
              Done Tagging
            </Button>
          </ActionContainer>
        )}
        {errorSavingTags && (
          <ErrorSection>
            <span>
              <Icon icon="bell" />
            </span>
            <span>An error occurred while saving, please try again.</span>
          </ErrorSection>
        )}
      </div>
    );
  }
}

BulkSegmentAllocator.propTypes = {
  clientCategories: PropTypes.arrayOf(PropTypes.object).isRequired, // eslint-disable-line
  clientId: PropTypes.string.isRequired,
  dataEntries: PropTypes.arrayOf(PropTypes.object).isRequired, // eslint-disable-line
  isOverridingSegments: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

BulkSegmentAllocator.defaultProps = {
  isOverridingSegments: false,
};

export default BulkSegmentAllocator;
